import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { convertColorToThemeHexCode } from 'Styles/colors.util';
import CountryPickerLink from '../CountryPickerLink/CountryPickerLink';

import { ALERT_BAR } from './Alertbar.gql';
import { isNewVisitor } from './Alertbar.utils';
import sanitizeHTML from '../../util/sanitize-html';
import { isPreview } from '../../graphql/previews';

import * as Styled from './Alertbar.styled';

const Alertbar = ({
  textAlignment,
  alertTitle,
  alertLink,
  alertBackgroundColor,
  alertCustomBackgroundColor
}) => (
  <Styled.Alertbar
    data-track-region="alert bar"
    textAlignment={textAlignment.toLowerCase()}
    alertbarBackgroundColor={
      alertCustomBackgroundColor ||
      convertColorToThemeHexCode(alertBackgroundColor)
    }
    data-label="alertBar"
  >
    <Styled.AlertbarMobileParagraph
      data-testid="Alert__mobile-title"
      // Supports custom text styling in CMS
      dangerouslySetInnerHTML={{
        __html: alertLink
          ? `<a href=${alertLink} data-track-label="alert bar link">${alertTitle?.mobile}</a>`
          : alertTitle?.mobile
      }}
    />
    <Styled.AlertbarDesktopParagraph
      data-testid="Alert__desktop-title"
      // Supports custom text styling in CMS
      dangerouslySetInnerHTML={{
        __html: alertLink
          ? `<a href=${alertLink} data-track-label="alert bar link">${alertTitle?.desktop}</a>`
          : alertTitle?.desktop
      }}
    />
    <Styled.DesktopCountryPickerWrapper>
      <CountryPickerLink visualType="globe" />
    </Styled.DesktopCountryPickerWrapper>
  </Styled.Alertbar>
);

const AlertbarWithData = (props) => {
  const messageType = isNewVisitor()
    ? 'newVisitorMessage'
    : 'returningVisitorMessage';

  const { data } = useQuery(ALERT_BAR, {
    variables: { preview: isPreview('alertbar') }
  });

  if (!data?.alertBar)
    return (
      <Styled.AlertbarBlock
        alertbarBackgroundColor={convertColorToThemeHexCode(
          props.alertBackgroundColor
        )}
      />
    );

  const alertTitle = {
    mobile: sanitizeHTML(data.alertBar[messageType].mobileCopy),
    desktop: sanitizeHTML(data.alertBar[messageType].desktopCopy)
  };
  const alertLink = data.alertBar[messageType].link;

  return (
    <Alertbar
      textAlignment={data?.alertBar.textAlignment}
      alertTitle={alertTitle}
      alertLink={alertLink}
      alertBackgroundColor={convertColorToThemeHexCode(
        data?.alertBar.backgroundColor
      )}
      alertCustomBackgroundColor={data?.alertBar.customBackgroundColor}
      {...props}
    />
  );
};

AlertbarWithData.propTypes = { alertBackgroundColor: PropTypes.string };

AlertbarWithData.defaultProps = { alertBackgroundColor: 'bombasBlue' };

Alertbar.propTypes = {
  textAlignment: PropTypes.string,
  alertTitle: PropTypes.shape({
    mobile: PropTypes.string,
    desktop: PropTypes.string
  }).isRequired,
  alertLink: PropTypes.string,
  alertBackgroundColor: PropTypes.string,
  alertCustomBackgroundColor: PropTypes.string
};

Alertbar.defaultProps = {
  textAlignment: undefined,
  alertLink: undefined,
  alertBackgroundColor: 'bombasBlue',
  alertCustomBackgroundColor: undefined
};

export { AlertbarWithData };
export default Alertbar;
